import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useLabel } from '@/lib/labels';
import { useHolidays } from '@/hooks/useHolidays';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { ORIGIN_LABELS, STATUS_LABELS } from '@/lib/shift-format';

/**
 * `/shift/my` (v0.4 章④ F-1、D-110/§4-11)。パート層の pull 経路として最も利用頻度の高い画面。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-11
 *
 * ⚠️ D-198 (v0.4 F-1): D-110 の `user_toggle` モードは本来「per-user のサーバ設定」に永続化する
 * 設計 (v04-handoff-next-session.md §0-J-3) だが、そのための新設テーブル・API は F-1 の
 * 「土台+最短の縦切り」の範囲を超えるため今回は見送り、セッション内 (React state) のみのトグルに
 * 留める (端末を跨いだ保持は行われない)。tenant 設定自体の解決 (always_show/always_hide) は
 * routes/shift-misc.ts の `GET /shift/my` が返す `time_display_mode` に従っており、これは
 * 正しく機能する。永続化は F-2 以降、`/settings/shift-patterns` 等で time_display_mode の
 * テナント設定 UI を作るタイミングで一緒に着手すること。
 */

type Assignment = {
  assignment_id: string;
  work_date: string;
  start_at: string;
  end_at: string;
  break_minutes: number | null;
  work_node_id: string;
  home_node_id: string | null;
  origin: string;
  status: string;
  note: string | null;
};

type MyShiftResponse = {
  time_display_mode: 'always_show' | 'always_hide' | 'user_toggle';
  assignments: Assignment[];
};

const WINDOW_DAYS = 14;

const toDateOnly = (d: Date): string => d.toISOString().slice(0, 10);
const addDays = (yyyyMmDd: string, days: number): string => {
  const d = new Date(`${yyyyMmDd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return toDateOnly(d);
};
const formatTimeOfDay = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function ShiftMyPage() {
  const t = useLabel();
  const [anchor, setAnchor] = useState(() => toDateOnly(new Date()));
  const [showTimesOverride, setShowTimesOverride] = useState<boolean | null>(null);

  const from = anchor;
  const to = addDays(anchor, WINDOW_DAYS - 1);
  const holidays = useHolidays(from, to);

  const { data, isLoading, isError } = useQuery<MyShiftResponse>({
    queryKey: ['shift-my', from, to],
    queryFn: () => apiFetch(`/shift/my?from=${from}&to=${to}`),
  });

  const mode = data?.time_display_mode ?? 'always_show';
  const showTimes = mode === 'always_show' || (mode === 'user_toggle' && (showTimesOverride ?? true));

  const grouped = useMemo(() => {
    const map = new Map<string, Assignment[]>();
    for (const a of data?.assignments ?? []) {
      const list = map.get(a.work_date);
      if (list) list.push(a); else map.set(a.work_date, [a]);
    }
    return [...map.entries()].sort(([d1], [d2]) => d1.localeCompare(d2));
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-h2">自分の{t('entity.shift_assignments', 'シフト')}</h2>
          <p className="text-sm text-text-sub mt-1">表示している内容は当社分のみです。掛け持ち先の勤務時間は含まれません。</p>
        </div>
        {mode === 'user_toggle' && (
          <Button
            variant="secondary" size="sm"
            leftIcon={showTimes ? <EyeOff size={14} /> : <Eye size={14} />}
            onClick={() => setShowTimesOverride(!showTimes)}
          >
            {showTimes ? '時刻を隠す' : '時刻を表示する'}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost" size="sm" leftIcon={<ChevronLeft size={16} />} aria-label="前の期間"
          onClick={() => setAnchor((cur) => addDays(cur, -WINDOW_DAYS))}
        >
          <span className="hidden sm:inline">前の期間</span>
        </Button>
        <div className="text-sm font-semibold text-text-body text-center">
          <DateLabel date={from} withWeekday holidays={holidays} /> 〜 <DateLabel date={to} withWeekday holidays={holidays} />
        </div>
        <Button
          variant="ghost" size="sm" rightIcon={<ChevronRight size={16} />} aria-label="次の期間"
          onClick={() => setAnchor((cur) => addDays(cur, WINDOW_DAYS))}
        >
          <span className="hidden sm:inline">次の期間</span>
        </Button>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">シフトの取得に失敗しました。</div>
      ) : grouped.length === 0 ? (
        <EmptyState
          message="この期間に確定したシフトはまだありません"
          description="シフトが確定すると、ここに勤務日と時間帯が表示されます。期間を切り替えて他の週も確認できます。"
        />
      ) : (
        <div className="space-y-3">
          {grouped.map(([date, list]) => (
            <div key={date} className="border border-surface-border rounded-lg bg-white overflow-hidden">
              <div className="px-4 py-2 text-sm font-semibold text-text-body bg-surface-sunken border-b border-surface-border">
                <DateLabel date={date} withWeekday holidays={holidays} />
              </div>
              <div className="divide-y divide-surface-border">
                {list.map((a) => {
                  // ⚠️ D-110: 応援中の枠は時刻表示モードに関わらず常に時刻を表示する
                  // (応援先の営業開始時刻を知らない可能性があるため、非表示にすると実害になる)
                  const isSupport = a.home_node_id !== null && a.work_node_id !== a.home_node_id;
                  const revealTimes = showTimes || isSupport;
                  return (
                    <div key={a.assignment_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm text-text-body">
                          {revealTimes ? `${formatTimeOfDay(a.start_at)} 〜 ${formatTimeOfDay(a.end_at)}` : '時間帯は非公開です'}
                        </div>
                        <div className="text-xs text-text-sub mt-0.5 flex flex-wrap gap-x-2">
                          {isSupport && <span className="text-alert-text font-semibold">応援</span>}
                          <span>{ORIGIN_LABELS[a.origin] ?? a.origin}</span>
                          {a.status !== 'confirmed' && <span>{STATUS_LABELS[a.status] ?? a.status}</span>}
                          {a.note && <span className="truncate">{a.note}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
